import React, { useState, useEffect } from "react";
import { Send, Bot, User, Lightbulb, Target, TrendingUp } from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";

/**
 * AI Agent Page Component
 * Interface for generating marketing strategies using AI
 */
const AIAgent = () => {
  const [goal, setGoal] = useState("");
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);

  const exampleGoals = [
    "Increase website leads by 25% in the next quarter",
    "Improve Google Ads conversion rate from 2% to 4%",
    "Reduce cost per acquisition by $15 while maintaining lead volume",
    "Launch a new product campaign targeting millennials",
    "Optimize our email marketing funnel for better engagement",
  ];

  /**
   * Generate initial marketing plan
   */
  const generatePlan = async () => {
    if (!goal.trim()) {
      toast.error("Please enter your marketing goal");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post("/api/ai/generate-plan", {
        goal: goal.trim(),
      });

      const newPlan = response.data.plan;
      setCurrentPlan(newPlan);

      // Add initial messages to conversation
      const userMessage = {
        id: Date.now(),
        type: "user",
        content: goal.trim(),
        timestamp: new Date(),
      };

      const aiMessage = {
        id: Date.now() + 1,
        type: "ai",
        content: newPlan,
        timestamp: new Date(),
      };

      setConversation([userMessage, aiMessage]);
      setGoal("");

      toast.success("Marketing plan generated successfully!");
    } catch (error) {
      console.error("Failed to generate plan:", error);
      toast.error("Failed to generate marketing plan");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send refinement message to AI
   */
  const sendMessage = async (message) => {
    if (!message.trim() || !currentPlan) return;

    try {
      const userMessage = {
        id: Date.now(),
        type: "user",
        content: message.trim(),
        timestamp: new Date(),
      };

      setConversation((prev) => [...prev, userMessage]);
      setLoading(true);

      const response = await axios.post("/api/ai/refine-plan", {
        planId: currentPlan.id,
        message: message.trim(),
        conversationHistory: conversation,
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: "ai",
        content: response.data.refinedPlan,
        timestamp: new Date(),
      };

      setConversation((prev) => [...prev, aiMessage]);
      setCurrentPlan(response.data.plan);
    } catch (error) {
      console.error("Failed to refine plan:", error);
      toast.error("Failed to refine plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">AI Marketing Agent</h1>
        <p className="text-gray-600">
          Get personalized marketing strategies based on your data and goals
        </p>
      </div>

      {/* Goal Input Section */}
      {!currentPlan && (
        <div className="card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              What's your marketing goal?
            </h2>
          </div>

          <div className="space-y-4">
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Describe your marketing goal in detail. For example: 'Increase qualified leads by 30% for our B2B SaaS product in the next quarter while maintaining a CPA under $100'"
              className="w-full h-24 input-field resize-none"
              maxLength={500}
            />

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {goal.length}/500 characters
              </span>
              <button
                onClick={generatePlan}
                disabled={loading || !goal.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-4 h-4" />
                    <span>Generate Strategy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Example Goals */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Example goals:
            </h3>
            <div className="space-y-2">
              {exampleGoals.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setGoal(example)}
                  className="text-left w-full p-3 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  "{example}"
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Conversation Interface */}
      {currentPlan && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Interface */}
          <div className="lg:col-span-2 card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Marketing Strategy Chat
                </h2>
              </div>

              <button
                onClick={() => {
                  setCurrentPlan(null);
                  setConversation([]);
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Start New Plan
              </button>
            </div>

            {/* Messages */}
            <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
              {conversation.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 ${message.type === "user" ? "flex-row-reverse space-x-reverse" : ""}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === "user"
                        ? "bg-primary-100"
                        : "bg-green-100"
                    }`}
                  >
                    {message.type === "user" ? (
                      <User className="w-4 h-4 text-primary-600" />
                    ) : (
                      <Bot className="w-4 h-4 text-green-600" />
                    )}
                  </div>

                  <div
                    className={`flex-1 ${message.type === "user" ? "text-right" : ""}`}
                  >
                    <div
                      className={`inline-block p-3 rounded-lg max-w-xs lg:max-w-md ${
                        message.type === "user"
                          ? "bg-primary-600 text-white"
                          : "bg-gray-100 text-gray-900"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <MessageInput onSend={sendMessage} loading={loading} />
          </div>

          {/* Current Plan Summary */}
          <div className="space-y-4">
            <div className="card p-4">
              <div className="flex items-center space-x-2 mb-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Current Plan</h3>
              </div>

              <div className="text-sm text-gray-600 space-y-2">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-900 mb-1">Goal:</p>
                  <p className="text-blue-800">{conversation[0]?.content}</p>
                </div>

                <div className="space-y-2">
                  <p className="font-medium text-gray-900">Key Strategies:</p>
                  <ul className="text-xs space-y-1 text-gray-600">
                    <li>• Multi-channel campaign optimization</li>
                    <li>• Audience targeting refinement</li>
                    <li>• Conversion funnel improvements</li>
                    <li>• Performance tracking setup</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Suggested Refinements
              </h3>
              <div className="space-y-2">
                {[
                  "Adjust budget allocation",
                  "Refine target audience",
                  "Add email marketing",
                  "Include social media",
                ].map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => sendMessage(suggestion)}
                    disabled={loading}
                    className="w-full text-left p-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded border border-gray-200 hover:border-gray-300 transition-colors disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Message Input Component
 */
const MessageInput = ({ onSend, loading }) => {
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !loading) {
      onSend(message);
      setMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex space-x-2">
      <input
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder="Ask for refinements or changes..."
        className="flex-1 input-field"
        disabled={loading}
      />
      <button
        type="submit"
        disabled={loading || !message.trim()}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed p-2"
      >
        <Send className="w-4 h-4" />
      </button>
    </form>
  );
};

export default AIAgent;
